import { useEffect, useState } from "react";
import axios from "axios";
import { Search, AlertCircle, Pencil, Plus, Minus, X, Check, Loader2, Printer, Share2, FileText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Support numbers appear in the printed footer + WhatsApp export.
const SUPPORT_PHONES = [
  { local: "07717637525", label: "خدمة العملاء" },
  { local: "07842220088", label: "الدعم الفني" },
];

const AccountBalances = () => {
  const [q, setQ] = useState("");
  const [debtorsOnly, setDebtorsOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/account-balances`, {
        params: { q, debtors_only: debtorsOnly },
      });
      setData(res);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(fetchData, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, debtorsOnly]);

  // ─── Print / PDF export ─────────────────────────────────────
  // بيبني تقرير أنيق شبيه بفاتورة الشركة: هيدر + جدول ديون + مجموع + فوتر
  // مع أرقام الدعم. يشتغل مع الفلاتر الحالية (بحث + مدينون فقط).
  const buildPrintHtml = () => {
    if (!data) return "";
    const dateStr = new Date().toLocaleString("ar-IQ", {
      dateStyle: "medium",
      timeStyle: "short",
    });
    const filterStr = [
      q.trim() && `بحث: "${q.trim()}"`,
      debtorsOnly && "المدينون فقط",
    ].filter(Boolean).join(" · ") || "الكل";

    const rows = (data.customers || []).map((u, i) => {
      const bal = Number(u.customer_debt) || 0;
      const status =
        bal > 0 ? '<span class="pill pill-red">دين على الزبون</span>' :
        bal < 0 ? '<span class="pill pill-blue">يطلبنا</span>' :
                  '<span class="pill pill-green">مسدّد</span>';
      const cls = bal > 0 ? "amt-red" : bal < 0 ? "amt-blue" : "amt-green";
      const name = (u.full_name || "").replace(/</g, "&lt;");
      const market = u.market_name ? ` — ${u.market_name.replace(/</g, "&lt;")}` : "";
      const phone = (u.phone || "").replace(/</g, "&lt;");
      const iPad = u.customer_id ? `ايباد #${u.customer_id}` : "";
      return `<tr>
        <td class="c">${i + 1}</td>
        <td>${name}${market}${iPad ? `<div class="sub">${iPad}</div>` : ""}</td>
        <td class="c" dir="ltr">${phone}</td>
        <td class="c ${cls}" dir="ltr">${fmt(Math.abs(bal))} د.ع</td>
        <td class="c">${status}</td>
      </tr>`;
    }).join("");

    const totalDebt = fmt(data.total_debt);
    const supportHtml = SUPPORT_PHONES.map(p => `${p.label}: <b dir="ltr">${p.local}</b>`).join(" · ");

    return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/>
      <title>كشف أرصدة الحسابات — جملة أون لاين</title>
      <style>
        @page { size: A4; margin: 12mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: 'Tajawal','Tahoma','Segoe UI',Arial,sans-serif; padding: 20px; color: #111; direction: rtl; margin: 0; }
        .header {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding-bottom: 14px; border-bottom: 3px solid #E85D45; margin-bottom: 14px;
        }
        .brand h1 { color: #E85D45; margin: 0; font-size: 24px; letter-spacing: 0.5px; }
        .brand p { margin: 4px 0 0; color: #5C6E64; font-size: 12px; }
        .date-box {
          text-align: left; font-size: 11px; color: #5C6E64;
          background: #F8FAFC; border: 1px solid #E4EBE6; border-radius: 10px; padding: 8px 12px;
        }
        .date-box b { display: block; color: #1E3A5F; font-size: 13px; }
        .summary {
          display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-bottom: 14px;
        }
        .summary .card {
          background: linear-gradient(90deg,#E85D45,#c14a36); color: #fff;
          border-radius: 12px; padding: 10px 12px; text-align: center;
        }
        .summary .card.blue { background: linear-gradient(90deg,#1E3A5F,#3B82F6); }
        .summary .card.green { background: linear-gradient(90deg,#15803D,#22C55E); }
        .summary .card p { margin: 0; }
        .summary .card .lbl { font-size: 10px; opacity: 0.9; }
        .summary .card .val { font-size: 18px; font-weight: 900; margin-top: 2px; }
        .filters { font-size: 11px; color: #5C6E64; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
        thead th { background: #E85D45; color: #fff; padding: 8px 6px; text-align: right; }
        thead th.c { text-align: center; }
        tbody td { border: 1px solid #E4EBE6; padding: 6px; vertical-align: middle; }
        tbody td.c { text-align: center; }
        tbody tr:nth-child(even) td { background: #FEFAF9; }
        .sub { font-size: 10px; color: #5C6E64; margin-top: 2px; }
        .pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 800; }
        .pill-red { background: #FEE2E2; color: #B91C1C; }
        .pill-blue { background: #DBEAFE; color: #1E3A5F; }
        .pill-green { background: #DCFCE7; color: #15803D; }
        .amt-red { color: #B91C1C; font-weight: 900; }
        .amt-blue { color: #1E3A5F; font-weight: 900; }
        .amt-green { color: #15803D; font-weight: 900; }
        .grand-row td {
          background: #1E3A5F !important; color: #fff !important;
          font-weight: 900; font-size: 14px;
        }
        .footer {
          margin-top: 24px; padding-top: 12px; border-top: 2px dashed #E4EBE6;
          text-align: center; font-size: 11px; color: #5C6E64; line-height: 1.7;
        }
        .footer .brand-line { color: #E85D45; font-weight: 900; font-size: 14px; }
        @media print { .noprint { display: none; } }
      </style></head>
      <body>
        <div class="header">
          <div class="brand">
            <h1>جملة أون لاين</h1>
            <p>كشف أرصدة الحسابات · Jumlaty Online</p>
          </div>
          <div class="date-box">
            <b>تاريخ الطباعة</b>
            <span dir="ltr">${dateStr}</span>
          </div>
        </div>
        <div class="summary">
          <div class="card blue">
            <p class="lbl">عدد المعروضين</p>
            <p class="val" dir="ltr">${fmt(data.count)}</p>
          </div>
          <div class="card">
            <p class="lbl">عدد المدينين</p>
            <p class="val" dir="ltr">${fmt(data.debtors_count)}</p>
          </div>
          <div class="card green">
            <p class="lbl">إجمالي الديون</p>
            <p class="val" dir="ltr">${totalDebt} د.ع</p>
          </div>
        </div>
        <div class="filters">
          الفلاتر المطبقة: <b>${filterStr}</b>
        </div>
        <table>
          <thead>
            <tr>
              <th class="c" style="width:34px">#</th>
              <th>اسم الزبون / الماركت</th>
              <th class="c" style="width:110px">الهاتف</th>
              <th class="c" style="width:130px">الرصيد</th>
              <th class="c" style="width:120px">الحالة</th>
            </tr>
          </thead>
          <tbody>
            ${rows || `<tr><td colspan="5" class="c" style="padding:20px;color:#5C6E64">لا توجد سجلات</td></tr>`}
            <tr class="grand-row">
              <td colspan="3" style="text-align:right">إجمالي الديون المفتوحة</td>
              <td class="c" dir="ltr">${totalDebt} د.ع</td>
              <td class="c" dir="ltr">${fmt(data.debtors_count)} زبون</td>
            </tr>
          </tbody>
        </table>
        <div class="footer">
          <div class="brand-line">جملة أون لاين — Jumlaty Online</div>
          <div>${supportHtml}</div>
          <div>الموقع: jumlaty.online</div>
        </div>
        <script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
      </body></html>`;
  };

  const handlePrint = () => {
    if (!data || (data.customers || []).length === 0) {
      toast.error("لا توجد بيانات للطباعة");
      return;
    }
    const w = window.open("", "_blank", "width=900,height=980");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    w.document.open();
    w.document.write(buildPrintHtml());
    w.document.close();
    w.focus();
  };

  const handleWhatsApp = () => {
    if (!data || (data.customers || []).length === 0) {
      toast.error("لا توجد بيانات للمشاركة");
      return;
    }
    const dateStr = new Date().toLocaleDateString("ar-IQ");
    const lines = [];
    lines.push("*📋 كشف أرصدة الحسابات — جملة أون لاين*");
    lines.push(`التاريخ: ${dateStr}`);
    if (debtorsOnly) lines.push("_الفلتر: المدينون فقط_");
    lines.push("─".repeat(22));
    (data.customers || []).forEach((u, i) => {
      const bal = Number(u.customer_debt) || 0;
      const status = bal > 0 ? "⚠️ دين" : bal < 0 ? "💰 يطلبنا" : "✓ مسدّد";
      const name = u.full_name + (u.market_name ? ` — ${u.market_name}` : "");
      lines.push(`${i + 1}. ${name}`);
      lines.push(`   📞 ${u.phone}  ·  ${fmt(Math.abs(bal))} د.ع  ${status}`);
    });
    lines.push("─".repeat(22));
    lines.push(`👥 عدد المدينين: *${fmt(data.debtors_count)}*`);
    lines.push(`💵 إجمالي الديون: *${fmt(data.total_debt)} د.ع*`);
    lines.push("");
    lines.push("*جملة أون لاين — Jumlaty Online*");
    SUPPORT_PHONES.forEach((p) => lines.push(`${p.label}: ${p.local}`));
    lines.push("🌐 jumlaty.online");
    const url = `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="account-balances">
      <div className="bg-white border-2 border-[#E85D45] rounded-2xl p-4 space-y-3">
        <div className="relative">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C6E64]" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث بالاسم/الماركت/الهاتف..."
            data-testid="balance-search"
            className="w-full pr-12 pl-4 py-3 rounded-2xl border border-[#E4EBE6] bg-white focus:outline-none focus:ring-2 focus:ring-[#E85D45] text-lg" />
        </div>
        <label
          htmlFor="balance-debtors-only"
          className={`flex items-center justify-between gap-3 cursor-pointer rounded-xl px-3 py-2 border-2 transition-colors ${
            debtorsOnly ? "bg-[#FEF2F2] border-[#E85D45]" : "bg-[#F9FAFB] border-[#E4EBE6]"
          }`}
          data-testid="balance-debtors-toggle"
        >
          <div className="flex items-center gap-2">
            <input
              id="balance-debtors-only"
              type="checkbox"
              checked={debtorsOnly}
              onChange={(e) => setDebtorsOnly(e.target.checked)}
              className="w-5 h-5 accent-[#E85D45] cursor-pointer"
            />
            <span className={`text-sm font-bold ${debtorsOnly ? "text-[#E85D45]" : "text-[#1E3A5F]"}`}>
              عرض الزبائن الذين عليهم دين فقط
            </span>
          </div>
          <span className="text-[10px] text-[#5C6E64]">
            {debtorsOnly ? "✓ مفعّل — يظهر المدينون فقط" : "بدون تفعيل — يظهر كل الزبائن"}
          </span>
        </label>
      </div>

      {data && (
        <div className="bg-gradient-to-l from-[#E85D45] to-[#c14a36] text-white rounded-2xl p-4 grid grid-cols-3 gap-2 text-center">
          <div><p className="text-[10px] opacity-90">المعروض</p><p className="font-bold text-xl">{fmt(data.count)}</p></div>
          <div><p className="text-[10px] opacity-90">عدد المدينين</p><p className="font-bold text-xl">{fmt(data.debtors_count)}</p></div>
          <div><p className="text-[10px] opacity-90">إجمالي الديون</p><p className="font-bold text-xl" dir="ltr">{fmt(data.total_debt)} د.ع</p></div>
        </div>
      )}

      {/* Export toolbar — طباعة / PDF / واتساب. يشتغل مع الفلاتر الحالية */}
      {data && data.customers && data.customers.length > 0 && (
        <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 flex flex-wrap gap-2 items-center justify-between">
          <p className="text-xs text-[#5C6E64] font-bold">
            📤 تصدير كشف الأرصدة ({fmt(data.count)} زبون):
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handlePrint}
              data-testid="balances-print-btn"
              title="طباعة كشف الأرصدة"
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5"
            >
              <Printer className="w-3.5 h-3.5" /> طباعة
            </button>
            <button
              onClick={handlePrint}
              data-testid="balances-pdf-btn"
              title="تصدير PDF (اختر 'حفظ كـPDF' من نافذة الطباعة)"
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5"
            >
              <FileText className="w-3.5 h-3.5" /> تصدير PDF
            </button>
            <button
              onClick={handleWhatsApp}
              data-testid="balances-whatsapp-btn"
              title="مشاركة عبر واتساب"
              className="bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-bold py-2 px-3 rounded-full flex items-center gap-1.5"
            >
              <Share2 className="w-3.5 h-3.5" /> واتساب
            </button>
          </div>
        </div>
      )}

      {loading ? <p className="text-center text-[#5C6E64]">جاري التحميل...</p> : (
        data && data.customers.length === 0 ? (
          <div className="text-center py-8 text-[#5C6E64]">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 text-[#7ED321]" />
            <p>{debtorsOnly ? "لا توجد ديون مفتوحة" : "لا يوجد زبائن"}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data?.customers.map((u) => (
              <BalanceRow
                key={u.id}
                u={u}
                editing={editingId === u.id}
                onStartEdit={() => setEditingId(u.id)}
                onCancelEdit={() => setEditingId(null)}
                onSaved={() => { setEditingId(null); fetchData(); }}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

// One row in the balances list. Has an inline editor that lets the admin
// type any custom balance (positive = owed by customer, negative = owed by
// us). Also supports quick + / - buttons for fast adjustments.
const BalanceRow = ({ u, editing, onStartEdit, onCancelEdit, onSaved }) => {
  const debt = Number(u.customer_debt) || 0;
  return (
    <div data-testid={`balance-row-${u.id}`} className="bg-white border border-[#E4EBE6] rounded-2xl p-3">
      <div className="flex justify-between items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-[#111814] truncate">
            {u.full_name}{u.market_name && ` — ${u.market_name}`}
          </p>
          <a href={`tel:${u.phone}`} className="text-xs text-[#5C6E64]" dir="ltr">{u.phone}</a>
          {u.customer_id && <span className="text-xs text-[#5C6E64] mr-2">ايباد #{u.customer_id}</span>}
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          <div>
            <p className={`font-bold text-lg ${debt > 0 ? "text-[#E85D45]" : debt < 0 ? "text-[#1E3A5F]" : "text-[#7ED321]"}`} dir="ltr">
              {fmt(Math.abs(debt))} د.ع
            </p>
            {debt < 0 && (
              <span className="text-[10px] font-bold text-[#1E3A5F] bg-[#DBEAFE] rounded-full px-2 py-0.5">
                يطلبنا
              </span>
            )}
            {debt > 0 && (
              <span className="text-[10px] text-[#E85D45]">دين على الزبون</span>
            )}
          </div>
          {!editing && (
            <button
              type="button"
              onClick={onStartEdit}
              data-testid={`balance-edit-${u.id}`}
              title="تعديل رصيد الزبون يدوياً"
              className="w-9 h-9 rounded-full bg-[#F3F6F4] hover:bg-[#1E3A5F] hover:text-white text-[#5C6E64] flex items-center justify-center transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {editing && (
        <BalanceEditor user={u} onCancel={onCancelEdit} onSaved={onSaved} />
      )}
    </div>
  );
};

// Inline editor — three modes:
//   • Quick +  : add to debt (customer owes more)
//   • Quick −  : subtract from debt (forgive / give credit)
//   • Set     : overwrite the entire balance with an exact value
const BalanceEditor = ({ user, onCancel, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("set"); // "set" | "add" | "sub"
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const handleSave = async () => {
    const n = Number(amount);
    if (!isFinite(n)) {
      toast.error("أدخل قيمة صحيحة");
      return;
    }
    setBusy(true);
    try {
      let body;
      if (mode === "set") body = { set: n, reason };
      else if (mode === "add") body = { adjust: Math.abs(n), reason };
      else body = { adjust: -Math.abs(n), reason };
      const { data } = await axios.patch(`${API}/admin/customer-debt/${user.id}`, body);
      const newVal = Number(data.new);
      toast.success(
        newVal === 0
          ? "تم تصفية الرصيد"
          : newVal > 0
            ? `الزبون عليه دين: ${fmt(Math.abs(newVal))} د.ع`
            : `الزبون يطلبنا: ${fmt(Math.abs(newVal))} د.ع`,
      );
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل التعديل");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#E4EBE6] space-y-2" data-testid={`balance-editor-${user.id}`}>
      {/* Mode selector — 3 pill buttons */}
      <div className="grid grid-cols-3 gap-1.5">
        <button
          type="button"
          onClick={() => setMode("set")}
          data-testid={`balance-mode-set-${user.id}`}
          className={`rounded-xl px-2 py-1.5 text-xs font-black transition-all ${
            mode === "set" ? "bg-[#1E3A5F] text-white shadow" : "bg-[#F3F6F4] text-[#5C6E64]"
          }`}
        >
          تعيين قيمة
        </button>
        <button
          type="button"
          onClick={() => setMode("add")}
          data-testid={`balance-mode-add-${user.id}`}
          className={`rounded-xl px-2 py-1.5 text-xs font-black flex items-center justify-center gap-1 transition-all ${
            mode === "add" ? "bg-[#E85D45] text-white shadow" : "bg-[#F3F6F4] text-[#5C6E64]"
          }`}
        >
          <Plus className="w-3 h-3" />
          إضافة دين
        </button>
        <button
          type="button"
          onClick={() => setMode("sub")}
          data-testid={`balance-mode-sub-${user.id}`}
          className={`rounded-xl px-2 py-1.5 text-xs font-black flex items-center justify-center gap-1 transition-all ${
            mode === "sub" ? "bg-[#15803D] text-white shadow" : "bg-[#F3F6F4] text-[#5C6E64]"
          }`}
        >
          <Minus className="w-3 h-3" />
          خصم
        </button>
      </div>

      {/* Amount input */}
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder={
          mode === "set"
            ? "اكتب الرصيد الجديد (موجب=دين / سالب=له رصيد)"
            : mode === "add"
              ? "اكتب المبلغ المضاف على الدين"
              : "اكتب المبلغ المخصوم من الدين"
        }
        data-testid={`balance-amount-${user.id}`}
        className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] text-right"
        dir="ltr"
        autoFocus
      />

      {/* Optional reason */}
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="سبب التعديل (اختياري)"
        data-testid={`balance-reason-${user.id}`}
        className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] text-sm text-right"
      />

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={busy || amount === ""}
          data-testid={`balance-save-${user.id}`}
          className="flex-1 bg-[#1E3A5F] hover:bg-[#152a47] disabled:opacity-50 text-white font-bold py-2 rounded-xl flex items-center justify-center gap-1.5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          حفظ
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`balance-cancel-${user.id}`}
          className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold px-4 rounded-xl"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[10px] text-[#5C6E64]">
        💡 <b>تعيين قيمة:</b> يستبدل الرصيد بالكامل (موجب = دين على الزبون، سالب = الزبون يطلبنا).{" "}
        <b>إضافة دين:</b> يزيد الدين.{" "}
        <b>خصم:</b> يقلل الدين (مفيد لإسقاط مبلغ أو إعطاء رصيد).
      </p>
    </div>
  );
};

export default AccountBalances;
